import { getBackendOnline, onBackendConnectivityChange } from "@/lib/api";
import { WifiOff } from "lucide-react";
import { useEffect, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";

function subscribeBrowserOnline(callback: () => void) {
	window.addEventListener("online", callback);
	window.addEventListener("offline", callback);
	return () => {
		window.removeEventListener("online", callback);
		window.removeEventListener("offline", callback);
	};
}

function getBrowserOnline() {
	return navigator.onLine;
}

function useBackendOnline() {
	return useSyncExternalStore(
		(callback) => onBackendConnectivityChange(() => callback()),
		getBackendOnline,
	);
}

export function OfflineBanner() {
	const { t } = useTranslation();
	const browserOnline = useSyncExternalStore(subscribeBrowserOnline, getBrowserOnline);
	const backendOnline = useBackendOnline();
	const offline = !browserOnline || !backendOnline;

	useEffect(() => {
		if (offline) {
			document.body.style.paddingTop = "48px";
		} else {
			document.body.style.paddingTop = "";
		}
		return () => {
			document.body.style.paddingTop = "";
		};
	}, [offline]);

	if (!offline) return null;

	return (
		<div
			role="alert"
			className="fixed inset-x-0 top-0 z-50 flex h-12 items-center justify-center gap-2 bg-red-900/90 text-sm text-primary backdrop-blur-sm"
		>
			<WifiOff className="h-4 w-4" aria-hidden="true" />
			<span>{t("error.connectionLost")}</span>
			<span className="text-secondary">&mdash;</span>
			<span className="text-secondary">{t("error.connectionLostDesc")}</span>
		</div>
	);
}
